/**
 * FormRequestPage (standalone form-request flow) styles — form-request-reskin.
 *
 * Ports docs/mockups/client-dashboard-redesign/form-request-flow.html (Frames 1–4)
 * onto the INJECTED per-tenant client tokens (never the mockup's literal :root hex).
 * The fixed page + keyboard-reactive scroller mirror the shipped DetailsPage /
 * ConfirmPage; the field + CTA primitives are the shipped `clientBooking`
 * appearance (FormRenderer) and the shared FlowBackButton. This module owns only
 * the flat pinned header (a sticky bar the content scrolls under), the request
 * intro, and the carded terminal screens. Radii/sizes are structural literals
 * (same discipline as DetailsPage.module.css); every colour derives from a token.
 */

/* ── Flat page + keyboard-reactive scroller (Frames 1–2) ── */
.flow {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Lock to the STABLE (max) viewport height so the page never collapses when the
     keyboard opens; the scroller below shrinks to the reactive height instead. */
  height: var(--app-viewport-stable-height, 100dvh);
  max-width: 480px;
  margin: 0 auto;
  overflow: hidden;
  background: var(--bg-page);
  color: var(--text-primary);
  font-family: var(--font-body);
}
.scroll {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  /* Reactive height: its bottom sits at the keyboard top so a focused field scrolls
     up naturally (same rail as DetailsPage .scroll). */
  height: var(--app-viewport-height, 100dvh);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  overscroll-behavior: none;
  overflow-anchor: none;
}
.scroll::-webkit-scrollbar {
  display: none;
}

/* ── Pinned header — a sticky glass-back + kicker + form-name bar the content
   scrolls under; its bottom edge is the hairline divider. Opaque bg so scrolled
   content disappears behind it. ── */
.head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  gap: 14px;
  padding: calc(
      var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) +
        var(--tg-content-safe-area-inset-top, 0px) + 12px
    )
    18px 12px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--bg-page);
}
.headBack {
  flex-shrink: 0;
}
.headText {
  flex: 1;
  min-width: 0;
  padding-bottom: 1px;
}
.kicker {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 3px;
}
.fname {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 21px;
  letter-spacing: -0.012em;
  line-height: 1.1;
  color: var(--text-primary);
  margin: 0;
  /* Two lines before an ellipsis: a 40-character name shows in full beside the back button. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* ── Scroll content ── */
.content {
  padding: 16px 20px calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 24px);
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.intro {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.req {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--accent);
}
.fdesc {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* Anonymous reassurance pill (the shipped .reservedInfo treatment). */
.anonPill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  padding: 7px 11px;
  align-self: flex-start;
  background: rgba(var(--accent-rgb), 0.1);
  border-radius: 10px;
  font-size: 12px;
  color: var(--accent);
}
.anonPill svg {
  width: 14px;
  height: 14px;
  stroke-width: 1.8;
  flex-shrink: 0;
}

/* ── Inline bottom CTA — the last item in the form's scroll stack (rendered via
   FormRenderer's renderActions). Not fixed: it flows after the fields so the
   keyboard never pushes it over content (shipped DetailsPage .formbar/.btnPrimary). ── */
.formbar {
  margin-top: 8px;
}
.submitError {
  margin: 0 0 10px;
  font-size: 13px;
  text-align: center;
  color: var(--color-error);
}
.btnPrimary {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: var(--glow-amber, 0 0 32px rgba(var(--accent-rgb), 0.28));
}
.btnPrimary:disabled {
  opacity: 0.6;
}
.btnPrimary.busy {
  opacity: 0.7;
}
.btnPrimary svg {
  width: 17px;
  height: 17px;
  stroke-width: 2.4;
}
.btnSpin {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2.4px solid rgba(255, 255, 255, 0.35);
  border-top-color: var(--text-on-accent);
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================================================
   TERMINAL SCREENS — success / consumed / unavailable / error (Frames 3–4).
   A centred strong-glass card (tapered ring) over the standard page atmosphere.
   ============================================================================ */
.page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-viewport-stable-height, 100dvh);
  max-width: 480px;
  margin: 0 auto;
  overflow: hidden;
  background: var(--bg-page);
  color: var(--text-primary);
  font-family: var(--font-body);
}
.atmosphere {
  position: absolute;
  inset: 0;
  z-index: 0;
  /* Documented composition (presets.ts): overlay darkening, then the atmosphere
     image layer, over the page colour. Matches ClientShell .atmosphere. */
  background:
    var(--page-overlay, none),
    var(--page-image, none),
    var(--bg-page);
  pointer-events: none;
}
.term {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px;
  text-align: center;
}
.termCard {
  position: relative;
  width: 100%;
  max-width: 330px;
  padding: 34px 24px 28px;
  border-radius: 24px;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
}
.termCard::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(var(--accent-light-rgb), 0.16) 0%,
    rgba(var(--accent-light-rgb), 0.03) 100%
  );
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.termCard > * {
  position: relative;
  z-index: 1;
}
.termOrb {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 28%,
    rgba(var(--accent-hot-rgb), 0.32) 0%,
    rgba(var(--accent-deep-rgb), 0.16) 100%
  );
  border: 1px solid rgba(var(--accent-rgb), 0.22);
  box-shadow: 0 0 42px rgba(var(--accent-rgb), 0.22);
  display: grid;
  place-items: center;
  color: var(--accent);
  margin-bottom: 22px;
}
.termOrb svg {
  width: 32px;
  height: 32px;
  stroke-width: 1.9;
}
.termKicker {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 10px;
}
.term h2 {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 27px;
  letter-spacing: -0.01em;
  color: var(--text-primary);
  margin: 0 0 9px;
}
.term p {
  font-size: 13.5px;
  color: var(--text-secondary);
  line-height: 1.55;
  max-width: 280px;
  margin: 0;
}
.termActions {
  width: 100%;
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.termBtn {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  margin: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 26px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: var(--glow-amber, 0 0 32px rgba(var(--accent-rgb), 0.28));
}
.termBtnSecondary {
  border: 1px solid var(--border-soft);
  background: var(--glass-bg);
  color: var(--text-primary);
  box-shadow: none;
}
.termBtn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2.2;
}
/* ── Loading state (themed spinner) ── */
.loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--text-secondary);
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--glass-bg-strong);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.loading p {
  margin: 0;
  font-size: 14px;
}
